/**
 * "Email settings" for dealers (flag REACT_APP_VENDOR_EMAIL_SETTINGS_ENABLED,
 * paired with the API app setting VendorEmailSettingsEnabled; FORSLAG-
 * VENDOR-EMAIL-STYRING 16/10 §2 + Nichlas' decisions 16/10 20:53).
 *
 * One rule per mail type on the DEALER org: which of the order's known
 * addresses (tokens) receive the mail as To or CC, plus up to five extra
 * addresses. A mail type WITHOUT a rule keeps today's recipients (the API's
 * default chain) - the API sends those as `defaults` so the screen can say
 * "Today: To placer" without any logic of its own.
 *
 * The Stripe payment mails are deliberately NOT a kind here (decision 1/6):
 * no row, no UI row, nothing on the wire.
 */

/**
 * A mail type a dealer can route - the wire token of the API's kinds[]
 * (orderConfirmation, vendorNewOrder, ...). An OPEN string on purpose: the
 * section renders whatever kinds the GET lists (a kind added on the API
 * side - e.g. the receipt of the dealer's OWN orders - shows up with the
 * API's `name` until it gets its own texts here), so an unknown kind never
 * breaks the screen or gets dropped from the PUT.
 */
export type EmailRoutingKind = string;

/** The kinds the screen has its own texts and switch mapping for. */
export type KnownEmailRoutingKind =
  | "orderConfirmation"
  | "vendorOrderConfirmation"
  | "vendorNewOrder"
  | "vendorPending"
  | "approvalRequest"
  | "orderShipped";

/**
 * The six known kinds in the screen's order (the fallback when the API sent
 * no kinds[] list - an older API / the tests' bare settings). The two
 * receipts share ONE sender: the API picks vendorOrderConfirmation when a
 * DEALER user placed the order (its own order, or one on a customer's
 * behalf - act-as), orderConfirmation when the customer's own user did
 * (API round 2, 17/10).
 */
export const EMAIL_ROUTING_KINDS: readonly KnownEmailRoutingKind[] = [
  "orderConfirmation",
  "vendorOrderConfirmation",
  "vendorNewOrder",
  "vendorPending",
  "approvalRequest",
  "orderShipped",
];

export function isKnownEmailRoutingKind(kind: string): kind is KnownEmailRoutingKind {
  return (EMAIL_ROUTING_KINDS as readonly string[]).includes(kind);
}

/**
 * One entry of the GET's kinds[] as the screen needs it: the wire token,
 * the API's English name (the title of a kind the screen has no texts for),
 * whether the kind can never be switched off (needs a To), and the
 * EmailPreference* switch it rides on (null = none = always on).
 */
export interface EmailRoutingKindInfo {
  kind: EmailRoutingKind;
  name: string | null;
  requiresTo: boolean;
  preferenceKey: string | null;
  /** The API's English explanation (round 2) - the tooltip of a kind the
   *  screen has no texts for. */
  help: string | null;
}

/**
 * The addresses an order/approval mail can be routed to, by name. The API
 * resolves them per mail: placer = the ordering user's login e-mail,
 * orderEmail = the e-mail written on the order (OrderDeliveryAddress.Email),
 * customerOrgMail = the customer org's main e-mail, vendorMain = the
 * dealer's main contact e-mail (IsMain contact -> first contact -> org
 * e-mail), vendorOrgMail = the dealer org's own e-mail field.
 */
export type EmailRoutingToken =
  | "placer"
  | "orderEmail"
  | "customerOrgMail"
  | "vendorMain"
  | "vendorOrgMail";

export const EMAIL_ROUTING_TOKENS: readonly EmailRoutingToken[] = [
  "placer",
  "orderEmail",
  "customerOrgMail",
  "vendorMain",
  "vendorOrgMail",
];

/** The four token columns of the screen (decision 8); vendorOrgMail is kept
 *  on the wire untouched but has no column in v1. */
export const EMAIL_ROUTING_COLUMN_TOKENS: readonly EmailRoutingToken[] = [
  "placer",
  "orderEmail",
  "customerOrgMail",
  "vendorMain",
];

export type EmailRoutingRole = "to" | "cc";

export interface EmailRoutingExtra {
  email: string;
  role: EmailRoutingRole;
}

/** One stored rule (one OrganizationConfigurations row on the API side). */
export interface EmailRoutingRule {
  to: EmailRoutingToken[];
  cc: EmailRoutingToken[];
  extra: EmailRoutingExtra[];
}

/**
 * Today's recipients of a kind when no rule exists (tokens only). ONE To
 * token = that address. MORE than one To token = a CHAIN, "the first that
 * is known on the order" (the shipped mail: orderEmail -> placer ->
 * customerOrgMail) - never a set, so the screen must NOT turn it into a
 * rule's To list (see effectiveRule).
 */
export interface EmailRoutingDefault {
  to: EmailRoutingToken[];
  cc: EmailRoutingToken[];
}

export type EmailRoutingRules = Partial<Record<EmailRoutingKind, EmailRoutingRule>>;

export interface VendorEmailSettings {
  /**
   * The API's verdict on whether THIS caller may change the settings
   * (admin, or the dealer org's primary contact). undefined = the API did
   * not say (older API) - the screen falls back to its own copy of the
   * rule (see resolveEmailSettingsCanEdit).
   */
  canEdit?: boolean;
  /**
   * The kinds the API listed, in ITS order (= the rows of the screen and
   * the kinds the PUT names). undefined = the API sent no list (an older
   * API / bare test settings) - the five known kinds are assumed.
   */
  kinds?: EmailRoutingKindInfo[];
  /** Only the kinds that HAVE a rule. */
  settings: EmailRoutingRules;
  /** Today's recipients per kind (the API's default chain, as tokens). */
  defaults: Partial<Record<EmailRoutingKind, EmailRoutingDefault>>;
  /** The resolved address behind a token where the API knows it up front
   *  (vendorMain, vendorOrgMail); order-bound tokens are null. */
  candidates: Partial<Record<EmailRoutingToken, string | null>>;
  /**
   * Per kind: may extra addresses be added? undefined = the API did not say
   * - the screen's own constant decides (ALLOWS_EXTRA_RECIPIENTS).
   */
  allowsExtraRecipients?: Partial<Record<EmailRoutingKind, boolean>>;
  /**
   * Per kind: the tokens the API accepts (the others are 400 forbidden-
   * token). undefined = the API did not say - the screen's own copy of the
   * API's table decides (DEFAULT_ALLOWED_TOKENS).
   */
  allowedTokens?: Partial<Record<EmailRoutingKind, EmailRoutingToken[]>>;
}
